// src/pages/PendingRequestsPage.js
import React, { useEffect, useState } from 'react'
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js'
import { useOutletContext } from 'react-router-dom'
import axios from 'axios'

function PendingRequestsPage() {
  const [requests, setRequests] = useState([])
  const [showPayModal, setShowPayModal] = useState(false)
  const [selectedRequest, setSelectedRequest] = useState(null)

  // Single message modal (success/error)
  const [showMessageModal, setShowMessageModal] = useState(false)
  const [modalMessage, setModalMessage] = useState('')

  const user = JSON.parse(localStorage.getItem('user')) || {}
  const stripe = useStripe()
  const elements = useElements()

  // 1) Retrieve lang from the parent (PrivateLayout) via Outlet context
  const { lang } = useOutletContext()

  // 2) Basic translations for this page
  const translations = {
    en: {
      pageTitle: 'Pending Requests',
      noPending: 'No pending requests.',
      requestor: 'Requestor',
      amount: 'Amount',
      date: 'Date',
      action: 'Action',
      pay: 'Pay',
      decline: 'Decline',
      confirmPayment: 'Confirm Payment',
      cancel: 'Cancel',
      paymentSucceeded: 'Payment succeeded!',
      errorFetching: 'Error fetching pending requests:',
      errorDeclining: 'Error declining request:',
      requestDeclined: 'Request declined',
      paymentFailed: 'Payment failed:',
      errorResponding: 'Error responding to request:',
      noClientSecret: 'No clientSecret returned',
      ok: 'OK',
    },
    ja: {
      pageTitle: '保留中のリクエスト',
      noPending: '保留中のリクエストはありません。',
      requestor: '依頼者',
      amount: '金額',
      date: '日時',
      action: '操作',
      pay: '支払う',
      decline: '拒否',
      confirmPayment: '支払いを確定',
      cancel: 'キャンセル',
      paymentSucceeded: '支払いが完了しました！',
      errorFetching: '保留中のリクエストの取得中にエラー:',
      errorDeclining: 'リクエストの拒否中にエラー:',
      requestDeclined: 'リクエストが拒否されました',
      paymentFailed: '支払いに失敗しました:',
      errorResponding: 'リクエスト応答中にエラー:',
      noClientSecret: 'clientSecretが返されませんでした',
      ok: 'OK',
    },
    pt: {
      pageTitle: 'Solicitações Pendentes',
      noPending: 'Sem solicitações pendentes.',
      requestor: 'Solicitante',
      amount: 'Valor',
      date: 'Data',
      action: 'Ação',
      pay: 'Pagar',
      decline: 'Recusar',
      confirmPayment: 'Confirmar Pagamento',
      cancel: 'Cancelar',
      paymentSucceeded: 'Pagamento bem-sucedido!',
      errorFetching: 'Erro ao buscar solicitações pendentes:',
      errorDeclining: 'Erro ao recusar solicitação:',
      requestDeclined: 'Solicitação recusada',
      paymentFailed: 'Falha no pagamento:',
      errorResponding: 'Erro ao responder à solicitação:',
      noClientSecret: 'Nenhum clientSecret retornado',
      ok: 'OK',
    },
  }

  // 3) Pick the correct translation
  const t = translations[lang] || translations.en

  useEffect(() => {
    if (!user.id) return
    fetchPendingRequests()
    // eslint-disable-next-line
  }, [])

  const fetchPendingRequests = async () => {
    try {
      const res = await axios.get(
        `https://payme-backend-713827320947.asia-northeast1.run.app/api/requests/pending/${user.id}`
      )
      setRequests(res.data)
    } catch (error) {
      showModal(`${t.errorFetching} ${error.message}`)
    }
  }

  // Show a message in the modal
  const showModal = (msg) => {
    setModalMessage(msg)
    setShowMessageModal(true)
  }

  // Close the single-message modal
  const handleCloseModal = () => {
    setModalMessage('')
    setShowMessageModal(false)
  }

  // Remove a request from local state so it disappears from the table
  const removeRequestFromState = (requestId) => {
    setRequests((prev) => prev.filter((r) => r.id !== requestId))
  }

  // Decline request
  const handleDecline = async (requestId) => {
    try {
      const res = await axios.post(
        'https://payme-backend-713827320947.asia-northeast1.run.app/api/requests/respond',
        {
          userId: user.id,
          requestId,
          action: 'decline',
        }
      )
      showModal(res.data.message || t.requestDeclined)
      removeRequestFromState(requestId)
    } catch (error) {
      showModal(`${t.errorDeclining} ${error.message}`)
    }
  }

  // Start paying => open pay modal with selected request
  const handleStartPay = (req) => {
    setSelectedRequest(req)
    setShowPayModal(true)
  }

  // Cancel pay => close modal, clear
  const handleCancelPay = () => {
    setSelectedRequest(null)
    setShowPayModal(false)
  }

  // Confirm Payment => same logic as before, but inside the modal
  const handleConfirmPayment = async () => {
    try {
      if (!selectedRequest) return
      const requestId = selectedRequest.id

      // 1) Create PaymentIntent
      const res = await axios.post(
        'https://payme-backend-713827320947.asia-northeast1.run.app/api/requests/respond',
        {
          userId: user.id,
          requestId,
          action: 'pay',
        }
      )

      const clientSecret = res.data.clientSecret
      if (!clientSecret) {
        showModal(res.data.message || t.noClientSecret)
        return
      }

      // 2) Confirm with Stripe
      const cardElement = elements.getElement(CardElement)
      const confirmResult = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: user.name,
            email: user.email,
          },
        },
      })

      if (confirmResult.error) {
        showModal(`${t.paymentFailed} ${confirmResult.error.message}`)
      } else if (confirmResult.paymentIntent?.status === 'succeeded') {
        // 3) finishPay
        const finishRes = await axios.post(
          'https://payme-backend-713827320947.asia-northeast1.run.app/api/requests/respond',
          {
            userId: user.id,
            requestId,
            action: 'finishPay',
          }
        )
        const successMsg = `${t.paymentSucceeded}\n${finishRes.data.message}`
        showModal(successMsg)

        // Remove from table, close modal
        removeRequestFromState(requestId)
        setShowPayModal(false)
        setSelectedRequest(null)
      }
    } catch (error) {
      showModal(`${t.errorResponding} ${error.message}`)
    }
  }

  // If user not logged in
  if (!user || !user.id) {
    return <p className="m-4 text-red-600">Please log in first.</p>
  }

  return (
    <div>
      <h2 className="mb-4 text-2xl font-semibold">{t.pageTitle}</h2>

      {requests.length === 0 ? (
        <p>{t.noPending}</p>
      ) : (
        <div className="relative overflow-x-auto shadow-md sm:rounded-lg">
          <table className="w-full text-sm text-left text-gray-500">
            <thead className="text-xs text-gray-700 uppercase bg-gray-100">
              <tr>
                <th scope="col" className="px-4 py-3">
                  {t.requestor}
                </th>
                <th scope="col" className="px-4 py-3">
                  {t.amount}
                </th>
                <th scope="col" className="px-4 py-3">
                  {t.date}
                </th>
                <th scope="col" className="px-4 py-3">
                  {t.action}
                </th>
              </tr>
            </thead>
            <tbody>
              {requests.map((req) => {
                const requestDate = req.created_at
                  ? new Date(req.created_at).toLocaleString()
                  : 'N/A'

                return (
                  <tr
                    key={req.id}
                    className="border-b bg-white hover:bg-gray-50"
                  >
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {req.fromUserName}
                    </td>
                    <td className="px-4 py-3">¥{req.amount}</td>
                    <td className="px-4 py-3">{requestDate}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => handleStartPay(req)}
                          className="bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700"
                        >
                          {t.pay}
                        </button>
                        <button
                          onClick={() => handleDecline(req.id)}
                          className="bg-red-600 text-white px-3 py-2 rounded hover:bg-red-700"
                        >
                          {t.decline}
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pay Modal => replicate SendMoney style */}
      {showPayModal && selectedRequest && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-2">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 relative">
            {/* Close button (top right) */}
            <button
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-800"
              onClick={handleCancelPay}
            >
              &times;
            </button>

            <h3 className="text-xl font-semibold mb-4">
              {t.confirmPayment}
            </h3>
            <p className="text-sm text-gray-700 mb-3">
              {t.requestor}: {selectedRequest.fromUserName}
            </p>
            <p className="text-sm text-gray-700 mb-3">
              {t.amount}: ¥{selectedRequest.amount}
            </p>

            {/* Card Element */}
            <div className="border border-gray-300 p-2 rounded mb-4">
              <CardElement />
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={handleCancelPay}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                {t.cancel}
              </button>
              <button
                onClick={handleConfirmPayment}
                disabled={!stripe || !elements}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                {t.confirmPayment}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Single message modal => success/error */}
      {showMessageModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-2">
          <div className="bg-white rounded-lg shadow-lg w-96 p-6 relative">
            <pre className="whitespace-pre-wrap text-gray-700 mb-4">
              {modalMessage}
            </pre>
            <div className="mt-4 flex justify-end">
              <button
                onClick={handleCloseModal}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
              >
                {t.ok}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default PendingRequestsPage
